import { AdvEmpty, AdvHeading, AdvListing, AdvSection, Btn, DirectoryFilters, IconCard, QuoteMarquee, useText } from '@kkf/design'
import {
  GARAGE_INVITATIONS,
  GARAGE_NEED,
  GARAGE_PROGRAMS,
  GARAGE_PROGRAMS_HEADING,
  GARAGE_TESTIMONIES,
  GARAGE_TESTIMONIES_HEADING,
} from '../data/garage'
import { useListingFilter } from '../components/useListingFilter'
import { useVocabulary } from '../hooks/useVocabulary'
import { useT } from '../i18n'

/**
 * The free side, searchable and filtered by track.
 *
 * Same shape as `/tours` and `/services`. What is different is who is reading it: a young
 * person, a parent, or a business considering a placement — so the page states that every
 * programme is free before it lists any of them, and the two invitations at the end are
 * the two different asks.
 *
 * **The three testimonies keep "testimony to be recorded" in their attribution.** They are
 * written placeholders, not recorded testimony, and an open blocker on the garage's PRD.
 * The marquee is a louder frame around them and a louder frame is not permission to let
 * them sound real.
 */
export function ProgrammesPage() {
  const t = useT()
  const word = useVocabulary()
  const s = useText()
  const f = useListingFilter({
    items: GARAGE_PROGRAMS,
    groupId: 'group',
    groupTitle: t('programmes.group'),
    groupOf: (p) => p.group,
    labelOf: word,
    // Both languages — see `ServicesPage`.
    searchText: (p) => `${p.title.en} ${p.title.mn} ${p.group} ${p.descr.en} ${p.descr.mn}`,
    order: ['Trade', 'Technology', 'Mentoring', 'Faith', 'Open to all'],
  })

  return (
    <>
      <AdvSection glow="bl" className="ul-adv-page-top" containerClassName="wow animate__fadeInUp">
        {/* This page's own name — the one <h1> on it. The garage's inner pages carry no
            breadcrumb banner, so the opening heading is the page heading. */}
        <AdvHeading
          eyebrow={s(GARAGE_PROGRAMS_HEADING.subTitle)}
          title={t('programmes.whatA')}
          titleAccent={t('programmes.canJoin')}
          lead={s(GARAGE_PROGRAMS_HEADING.descr)}
          as="h1"
        />
      </AdvSection>

      <AdvSection glow="tr" flush containerClassName="wow animate__fadeInUp">
        <AdvListing
          rail={
            <DirectoryFilters
              search={f.search}
              onSearchChange={f.setSearch}
              searchPlaceholder={t('programmes.search')}
              groups={f.groups}
              selected={f.selected}
              onToggle={f.toggle}
              onClear={f.clear}
              resultSummary={f.summary}
            />
          }
        >
          {f.results.length === 0 ? (
            <AdvEmpty onClear={f.clear}>
              No programme matches that. There are eight and every one of them is free —
              clearing the filters shows all of them.
            </AdvEmpty>
          ) : (
            <div className="row row-cols-lg-2 row-cols-1 ul-bs-row">
              {f.results.map((programme) => (
                <div className="col" key={s(programme.title)}>
                  <IconCard
                    item={{
                      title: s(programme.title),
                      descr: s(programme.descr),
                      image: programme.image,
                      imageAlt: s(programme.imageAlt),
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </AdvListing>
      </AdvSection>

      {/* WHY IT MATTERS — the UNICEF finding, attributed, beside the programmes it
          justifies rather than buried in a donor document. */}
      <AdvSection glow="tl" containerClassName="wow animate__fadeInUp">
        <AdvHeading
          eyebrow={s(GARAGE_NEED.subTitle)}
          title={t('programmes.trajectory')}
          titleAccent={t('programmes.trajectoryAccent')}
        />
        <div className="row row-cols-md-2 row-cols-1 gy-4 ul-bs-row align-items-start">
          <div className="col">
            {GARAGE_NEED.paragraphs.map((p) => (
              <p className="ul-adv-lead" key={s(p)}>
                {s(p)}
              </p>
            ))}
          </div>
          <div className="col">
            <div className="ul-adv-panel">
              <p className="ul-display-title ul-display-title--sm">{GARAGE_NEED.stat.figure}</p>
              <p className="ul-adv-lead">{GARAGE_NEED.stat.txt}</p>
              <p className="ul-adv-lead">
                <em>{GARAGE_NEED.stat.source}</em>
              </p>
            </div>
          </div>
        </div>
      </AdvSection>

      {/* FROM THE FLOOR — the placeholders, still labelled as placeholders. */}
      <AdvSection glow="br" containerClassName="wow animate__fadeInUp">
        <AdvHeading
          centered
          eyebrow={s(GARAGE_TESTIMONIES_HEADING.subTitle)}
          title={t('programmes.whatThe')}
          titleAccent={t('programmes.sayAccent')}
        />
      </AdvSection>
      <AdvSection flush>
        <QuoteMarquee
          rows={[
            GARAGE_TESTIMONIES.map((entry) => ({
              quote: s(entry.quote),
              by: s(entry.name),
              note: s(entry.role),
            })),
            [...GARAGE_TESTIMONIES].reverse().map((entry) => ({
              quote: s(entry.quote),
              by: s(entry.name),
              note: s(entry.role),
            })),
          ]}
        />
      </AdvSection>

      {/* THE TWO INVITATIONS — deliberately different asks: one costs a young person
          nothing, the other asks a business for time. */}
      <AdvSection glow="band" containerClassName="wow animate__fadeInUp">
        <AdvHeading centered eyebrow={t('programmes.threeWays')} title={t('programmes.comeIn')} titleAccent={t('programmes.orWork')} />
        <div className="row row-cols-lg-3 row-cols-md-2 row-cols-1 gy-4 ul-bs-row align-items-stretch">
          {GARAGE_INVITATIONS.map((inv) => (
            <div className="col" key={s(inv.title)}>
              <div className="ul-adv-panel h-100">
                <p className="ul-display-title ul-display-title--sm">{s(inv.title)}</p>
                <p className="ul-adv-lead">{s(inv.descr)}</p>
                <ul className="ul-spotlight-points">
                  {inv.list.map((l) => (
                    <li key={s(l)}>{s(l)}</li>
                  ))}
                </ul>
                <div className="ul-spotlight-btns">
                  {/* `ctaTo` is an in-app route and `ctaHref` a sibling app; the data
                      sets exactly one. No fallback: a missing target should fail the
                      typecheck, not silently send everybody to Partners, which is
                      what the previous version's default did. */}
                  {inv.ctaTo !== undefined ? (
                    <Btn to={inv.ctaTo} className="ul-btn--signal">
                      {s(inv.ctaLabel)}
                    </Btn>
                  ) : (
                    <Btn href={inv.ctaHref ?? '#'} className="ul-btn--signal">
                      {s(inv.ctaLabel)}
                    </Btn>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </AdvSection>
    </>
  )
}
